<template>
  <div
    :data-testid="sectionTag"
    class="w-chart-popover"
    :class="`w-chart-popover--${variant}`"
  >
    <template v-if="variant === 'compact'">
      <span
        :data-testid="`${sectionTag}-label`"
        class="w-chart-popover__compact-label label-regular-medium"
      >
        {{ label }}
      </span>
      <span class="w-chart-popover__compact-value-group">
        <span
          :data-testid="`${sectionTag}-value`"
          class="w-chart-popover__compact-value metric-bold-medium"
          :class="{ 'w-chart-popover__compact-value--rating': valueType === 'rating' }"
          :style="compactValueStyle"
        >
          {{ value }}
        </span>
        <v-icon
          v-if="icon === 'star'"
          :data-testid="`${sectionTag}-star-icon`"
          :icon="mdiStar"
          size="12"
          class="w-chart-popover__star"
          :style="compactValueStyle"
        />
      </span>
    </template>

    <template v-else>
      <p
        v-if="title"
        :data-testid="`${sectionTag}-title`"
        class="w-chart-popover__title label-semibold-medium"
      >
        {{ title }}
      </p>
      <ul v-if="metrics.length" class="w-chart-popover__rows">
        <li
          v-for="(metric, index) in metrics"
          :key="`${metric.label}-${index}`"
          :data-testid="`${sectionTag}-row`"
          class="w-chart-popover__row"
        >
          <span class="w-chart-popover__row-label-group">
            <WDotIndicator
              v-if="hasColorDots && metric.color"
              :color="metric.color"
              :section-tag="`${sectionTag}-dot`"
            />
            <span class="w-chart-popover__row-label label-regular-small">
              {{ metric.label }}
            </span>
          </span>
          <span class="w-chart-popover__row-value label-semibold-small">
            <span :style="getMetricValueStyle(metric)">
              {{ metric.value }}
            </span>
          </span>
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiStar } from '@mdi/js'
import WDotIndicator from './WDotIndicator.vue'
import { tokens } from '@/plugins/vuetify'

export type WChartPopoverVariant = 'default' | 'compact'
export type WChartPopoverValueType = 'number' | 'percentage' | 'rating' | 'currency'
export type WChartPopoverIcon = 'star' | null

export interface WChartPopoverMetric {
  label: string
  value: string | number
  color?: string
  valueColor?: string
}

export interface WChartPopoverProps {
  sectionTag?: string
  variant?: WChartPopoverVariant
  title?: string
  metrics?: WChartPopoverMetric[]
  label?: string
  value?: string | number
  valueType?: WChartPopoverValueType
  icon?: WChartPopoverIcon
  valueColor?: string
}

const props = withDefaults(defineProps<WChartPopoverProps>(), {
  sectionTag: 'unspecified',
  variant: 'default',
  title: '',
  metrics: () => [],
  label: '',
  value: '',
  valueType: 'number',
  icon: null,
  valueColor: '',
})

// Auto-show color dots when any metric carries a color (Figma usage rule:
// "use color dots only when the popover is comparing chart series").
const hasColorDots = computed(() => props.metrics.some((metric) => !!metric.color))

const compactValueStyle = computed(() => (props.valueColor ? { color: props.valueColor } : {}))

const getMetricValueStyle = (metric: WChartPopoverMetric) =>
  metric.valueColor ? { color: metric.valueColor } : {}
</script>

<style lang="scss" scoped>
.w-chart-popover {
  display: flex;
  width: max-content;
  max-width: 220px;
  background-color: v-bind('tokens.color.background.surface.default');
  border-radius: v-bind('tokens.borderRadius.layer.medium');
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.w-chart-popover--default {
  flex-direction: column;
  gap: v-bind('tokens.spacing.gap.small');
  padding: v-bind('tokens.spacing.inset.container.tight');
  min-width: 182px;
}

.w-chart-popover--compact {
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  padding-inline: v-bind('tokens.spacing.inset.container.tight');
  padding-block: v-bind('tokens.spacing.inset.container.extraTight');
  min-width: 171px;
}

.w-chart-popover__title {
  margin: 0;
  color: v-bind('tokens.color.content.text.primary');
  white-space: nowrap;
}

.w-chart-popover__rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: v-bind('tokens.spacing.gap.icon');
  width: 100%;
}

.w-chart-popover__row {
  display: flex;
  align-items: center;
  gap: v-bind('tokens.spacing.gap.medium');
}

.w-chart-popover__row-label-group {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: v-bind('tokens.spacing.gap.small');
}

.w-chart-popover__row-label {
  flex: 1 1 0;
  min-width: 0;
  color: v-bind('tokens.color.content.text.secondary');
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.w-chart-popover__row-value {
  flex-shrink: 0;
  color: v-bind('tokens.color.content.text.secondary');
  text-align: right;
  white-space: nowrap;
}

.w-chart-popover__compact-label {
  color: v-bind('tokens.color.content.text.secondary');
  white-space: nowrap;
}

.w-chart-popover__compact-value-group {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.w-chart-popover__compact-value {
  color: v-bind('tokens.color.content.text.primary');
  white-space: nowrap;
}

.w-chart-popover__compact-value--rating {
  color: v-bind('tokens.color.data.performance.excellent');
}

.w-chart-popover__star {
  color: v-bind('tokens.color.data.performance.excellent');
}
</style>
